/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {GenericObject} from '@ion/core/src/types/entities';
import {StatusOfflineCloudIcon} from '../../icons/StatusOfflineCloudIcon';
import {themedClassName} from '../../theme/helpers';
import {useThemeContext} from '../../theme/useThemeContext';
import {useIsAssetDetailsTile} from './assetDetailsTileContext';
import sharedStyles from './styles';

export type WidgetDescriptionProps = {
  label: string;
  value: string;
  messageDate: string;
  messageTime: string;
  offline?: boolean;
  valueStyle?: GenericObject
  style?: {
    [key: string]: string;
  };
};

/**
 * Defines Tire Temperature component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 * import React from 'react';
 * import {WidgetDescription} from '@ion/dls/src/components/smarterTrailer';
 *
 * <WidgetDescription label='Tire Temp'
 *   value='Normal'
 *   messageTime='January 1, 2021 8:20 AM' />
 *
 * ```
 */
export const WidgetDescription = ({
  label,
  value,
  messageDate,
  messageTime,
  offline = false,
  valueStyle,
  style
}: WidgetDescriptionProps) => {
  const classes       = sharedStyles();
  const {isCurrent}   = useThemeContext();
  const isTile        = useIsAssetDetailsTile();
  const isCurrentTile = isCurrent && isTile;
  let content         = null;
  if (value) {
    const dateClass   = themedClassName(classes.date, classes.currentDate, isCurrentTile);
    const description = (
      <>
        <div style={style}>
          <div className={themedClassName(classes.label, classes.currentLabel, isCurrentTile)}>
            {isCurrentTile ? label : `${label}: `}
          </div>
          <div className={themedClassName(classes.value, classes.currentValue, isCurrentTile)}
            style={valueStyle}>
            {offline && <StatusOfflineCloudIcon size={11} />}
            {value}
          </div>
        </div>
        <div className={dateClass}>
          {messageDate}
        </div>
        <div className={dateClass}>
          {messageTime}
        </div>
      </>
    );
    // the current theme's Asset Details tile lays its icon and description
    // out in a row, so the description keeps its own column there ...
    content = isCurrentTile ? (
      <div className={classes.currentDescription}>
        {description}
      </div>
    ) : description;
  }
  return content;
};

export default WidgetDescription;
